import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ImageIcon, Wand2, Download, X, Loader2, Sparkles,
  Lock, Crown, ChevronDown, RefreshCw, Scan, Clapperboard, User, History, Trash2, Building2, Star, Globe,
  ZoomIn, Maximize2, Bookmark, Share2, RotateCcw, Video
} from 'lucide-react';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';
import { PublishModal } from './VeritasContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ASPECT_RATIOS = [
  { label: '1:1', value: '1:1', desc: 'Cuadrado' },
  { label: '16:9', value: '16:9', desc: 'Panorámico' },
  { label: '9:16', value: '9:16', desc: 'Vertical' },
  { label: '4:3', value: '4:3', desc: 'Clásico' },
];

const STYLES = [
  { label: 'Fotorrealista', value: 'photorealistic' },
  { label: 'Ilustración', value: 'illustration' },
  { label: 'Arte digital', value: 'digital art' },
  { label: 'Pintura al óleo', value: 'oil painting' },
  { label: 'Anime', value: 'anime style' },
  { label: 'Minimalista', value: 'minimalist' },
  { label: 'Cinemático', value: 'cinematic' },
  { label: 'Sin estilo', value: '' },
];

const EXAMPLE_PROMPTS = [
  'Un astronauta explorando un bosque alienígena al atardecer',
  'Ciudad futurista bajo la lluvia, luces de neón reflejadas en el asfalto',
  'Retrato de un zorro ártico en la tundra nevada',
  'Interior minimalista de una cafetería japonesa al amanecer',
  'Dragón de cristal sobrevolando montañas envueltas en niebla',
];

export default function VeritasImage({ user, onNavigate }) {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('photorealistic');
  const [ratio, setRatio] = useState('1:1');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [error, setError] = useState(null);
  const [showStyleMenu, setShowStyleMenu] = useState(false);
  const [referenceImage, setReferenceImage] = useState(null); // base64
  const [referenceImagePreview, setReferenceImagePreview] = useState(null);
  const imageInputRef = useRef(null);

  const handleReferenceImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setReferenceImage(ev.target.result);
      setReferenceImagePreview(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const [generationCount, setGenerationCount] = useState(() => {
    if (user?.plan === 'pro' || user?.plan === 'business') return 0;
    const today = new Date().toDateString();
    try {
      const saved = JSON.parse(localStorage.getItem('veritas_image_count') || '{}');
      return saved.date === today ? (saved.count || 0) : 0;
    } catch { return 0; }
  });
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [imageHistory, setImageHistory] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem('veritas_image_history') || '[]');
      // Migrar entradas antiguas que tienen image base64 — quitarlo para liberar espacio
      const clean = raw.map(({ image, ...rest }) => rest);
      // Si había base64, limpiar el storage ahora
      if (raw.some(e => e.image)) {
        try { localStorage.setItem('veritas_image_history', JSON.stringify(clean)); } catch {
          try { localStorage.removeItem('veritas_image_history'); } catch {}
        }
      }
      return clean;
    } catch { return []; }
  });

  const isPro = user?.plan === 'pro' || user?.plan === 'business';
  const FREE_LIMIT = 3;
  const canGenerate = isPro || generationCount < FREE_LIMIT;

  const handleGenerate = async () => {
    if (!prompt.trim() || !canGenerate) return;
    setIsGenerating(true);
    setError(null);
    setGeneratedImage(null);

    try {
      const token = localStorage.getItem('veritas_token');
      const res = await fetch(`${API_URL}/api/image/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ prompt, style, aspect_ratio: ratio, image_base64: referenceImage || undefined }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Error al generar la imagen');
      }

      const data = await res.json();
      const imgUrl = data.image_url || data.image_base64;
      setGeneratedImage(imgUrl);
      setCurrentPrompt(prompt);
      // Guardar solo metadatos en historial — NO el base64 (demasiado grande para localStorage)
      const entry = { id: Date.now(), prompt, style, ratio, created_at: new Date().toISOString() };
      const updated = [entry, ...imageHistory].slice(0, 30);
      setImageHistory(updated);
      try {
        localStorage.setItem('veritas_image_history', JSON.stringify(updated));
      } catch (storageErr) {
        // Si aun así falla, limpiar y guardar solo la entrada más reciente
        try { localStorage.setItem('veritas_image_history', JSON.stringify([entry])); } catch {}
      }
      if (!isPro) {
        const newCount = generationCount + 1;
        setGenerationCount(newCount);
        localStorage.setItem('veritas_image_count', JSON.stringify({ date: new Date().toDateString(), count: newCount }));
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    const a = document.createElement('a');
    a.href = generatedImage;
    a.download = `veritas-image-${Date.now()}.png`;
    a.click();
  };

  const selectedStyle = STYLES.find(s => s.value === style) || STYLES[STYLES.length - 1];

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col">
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(168, 85, 247, 0.07), transparent 60%)' }} />

      {/* Header */}
      <header className="relative z-10 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          {/* Logo izquierda */}
          <button onClick={() => onNavigate('home')} className="flex items-center gap-2 hover:opacity-80 transition-opacity flex-shrink-0">
            <ImageIcon className="w-6 h-6 sm:w-7 sm:h-7 text-purple-500" />
            <h1 className="font-heading text-lg sm:text-xl font-black tracking-tight">VERITAS <span className="text-purple-500">IMAGE</span></h1>
          </button>

          {/* Product selector */}
          <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1">
            <button
              onClick={() => onNavigate('home')}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-mono text-xs font-bold uppercase tracking-wider transition-all text-zinc-500 hover:text-zinc-300"
            >
              <Scan className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">SCAN</span>
            </button>
            <button
              onClick={() => onNavigate('image')}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-mono text-xs font-bold uppercase tracking-wider transition-all bg-purple-600 text-white"
            >
              <ImageIcon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">IMAGE</span>
            </button>
            <button
              disabled
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-mono text-xs font-bold uppercase tracking-wider text-zinc-700 cursor-not-allowed"
              title="Próximamente"
            >
              <Clapperboard className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">VIDEO</span>
            </button>
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            {/* Plan pill */}
            {isPro && (
              user?.plan === 'business'
                ? <div className="hidden lg:flex items-center gap-1 px-3 py-1 rounded-full font-mono text-xs bg-purple-500/10 border border-purple-500/30 text-purple-400"><Building2 className="w-3 h-3" /> Business</div>
                : <div className="hidden lg:flex items-center gap-1 px-3 py-1 rounded-full font-mono text-xs bg-yellow-500/10 border border-yellow-500/30 text-yellow-400"><Star className="w-3 h-3" /> Pro</div>
            )}
            <button onClick={() => navigate('/blog')} className="hidden sm:flex items-center gap-1 px-3 py-2 text-zinc-400 hover:text-white font-mono uppercase tracking-wider text-xs sm:text-sm transition-all">
              Blog
            </button>
            <button onClick={() => onNavigate('precios')} className="hidden sm:flex items-center gap-1 px-3 py-2 text-zinc-400 hover:text-white font-mono uppercase tracking-wider text-xs sm:text-sm transition-all">
              Precios
            </button>
            <button onClick={() => setShowHistory(!showHistory)} className="flex items-center gap-1.5 px-2.5 sm:px-4 py-2 border border-zinc-700 hover:border-zinc-500 text-zinc-300 font-mono uppercase tracking-wider text-xs sm:text-sm rounded-sm transition-all">
              <History className="w-4 h-4" />
              <span className="hidden sm:inline">Historial</span>
              {imageHistory.length > 0 && <span className="px-1.5 py-0.5 bg-zinc-800 rounded-full text-xs">{imageHistory.length}</span>}
            </button>
            {user ? (
              <button onClick={() => onNavigate('profile')} className="flex items-center gap-1.5 px-2.5 sm:px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 font-mono text-xs sm:text-sm rounded-sm transition-all">
                <User className="w-4 h-4" /><span className="hidden sm:inline">{user.name?.split(' ')[0]}</span>
              </button>
            ) : (
              <button onClick={() => onNavigate('auth')} className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white font-mono uppercase tracking-wider text-xs sm:text-sm rounded-sm transition-all">
                <span className="hidden sm:inline">Iniciar sesión</span>
                <span className="sm:hidden">Entrar</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="relative z-10 flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-12">

        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 font-mono text-xs uppercase tracking-widest mb-4">
            <Sparkles className="w-3 h-3" /> Generador de imágenes IA
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight mb-3">
            VERITAS <span className="text-purple-500">IMAGE</span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-lg mx-auto">
            Describe lo que imaginas y la IA lo convierte en imagen en segundos.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">

          {/* LEFT: Controls */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5">

            {/* Prompt */}
            <div className="space-y-2">
              <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Descripción</label>
              <div className="relative">
                <textarea
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  placeholder="Describe la imagen que quieres crear..."
                  rows={5}
                  disabled={isGenerating}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-xl p-4 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-purple-500 font-mono text-sm transition-colors resize-none"
                />
                <span className="absolute bottom-3 right-3 text-zinc-600 font-mono text-xs">{prompt.length}</span>
              </div>
              {/* Example prompts */}
              <div className="flex flex-wrap gap-2">
                {EXAMPLE_PROMPTS.slice(0, 3).map((p, i) => (
                  <button key={i} onClick={() => setPrompt(p)}
                    className="text-xs text-zinc-500 hover:text-purple-400 border border-zinc-800 hover:border-purple-500/40 rounded-full px-3 py-1 transition-all font-mono truncate max-w-[200px]">
                    {p.substring(0, 30)}…
                  </button>
                ))}
              </div>
            </div>

            {/* Reference image */}
            <div className="space-y-2">
              <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Imagen de referencia <span className="text-zinc-700">(opcional)</span></label>
              <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleReferenceImage} />
              {referenceImagePreview ? (
                <div className="relative rounded-xl overflow-hidden border border-purple-500/30 bg-zinc-900">
                  <img src={referenceImagePreview} alt="Referencia" className="w-full max-h-40 object-cover" />
                  <button onClick={() => { setReferenceImage(null); setReferenceImagePreview(null); }}
                    className="absolute top-2 right-2 p-1.5 bg-zinc-900/80 backdrop-blur rounded-lg text-zinc-400 hover:text-red-400 border border-zinc-700 transition-all">
                    <X className="w-3.5 h-3.5" />
                  </button>
                  <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-purple-600/80 rounded-full font-mono text-xs text-white">Imagen cargada</div>
                </div>
              ) : (
                <button onClick={() => imageInputRef.current?.click()}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-zinc-900 border border-dashed border-zinc-700 hover:border-purple-500/50 rounded-xl text-zinc-500 hover:text-purple-400 transition-all">
                  <ImageIcon className="w-4 h-4 flex-shrink-0" />
                  <span className="font-mono text-sm">Subir imagen de referencia</span>
                </button>
              )}
            </div>

            {/* Style selector */}
            <div className="space-y-2">
              <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Estilo</label>
              <div className="relative">
                <button
                  onClick={() => setShowStyleMenu(v => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-zinc-900 border border-zinc-700 hover:border-zinc-500 rounded-xl text-sm font-mono text-zinc-300 transition-all"
                >
                  <span>{selectedStyle.label}</span>
                  <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${showStyleMenu ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {showStyleMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                      className="absolute top-full mt-1 left-0 right-0 bg-zinc-900 border border-zinc-700 rounded-xl overflow-hidden z-20 shadow-xl"
                    >
                      {STYLES.map(s => (
                        <button key={s.value} onClick={() => { setStyle(s.value); setShowStyleMenu(false); }}
                          className={`w-full text-left px-4 py-2.5 text-sm font-mono transition-colors ${style === s.value ? 'bg-purple-500/20 text-purple-300' : 'text-zinc-400 hover:bg-zinc-800'}`}>
                          {s.label}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Aspect ratio */}
            <div className="space-y-2">
              <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Proporción</label>
              <div className="flex gap-2">
                {ASPECT_RATIOS.map(r => (
                  <button key={r.value} onClick={() => setRatio(r.value)}
                    className={`flex-1 py-3 rounded-xl border font-mono text-sm transition-all ${ratio === r.value ? 'bg-purple-500/20 border-purple-500/50 text-purple-300' : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}>
                    <div className="font-bold">{r.label}</div>
                    <div className="text-xs opacity-60 mt-0.5">{r.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Free limit notice */}
            {!isPro && (
              <div className={`flex items-center gap-3 rounded-xl p-3 border text-sm font-mono ${
                generationCount >= FREE_LIMIT
                  ? 'bg-red-500/10 border-red-500/30 text-red-400'
                  : 'bg-zinc-900 border-zinc-800 text-zinc-500'
              }`}>
                {generationCount >= FREE_LIMIT ? <Lock className="w-4 h-4 flex-shrink-0" /> : <Sparkles className="w-4 h-4 flex-shrink-0" />}
                {generationCount >= FREE_LIMIT
                  ? <span>Límite gratuito alcanzado. <button onClick={() => onNavigate('precios')} className="text-yellow-400 underline">Actualiza a Pro</button> para generaciones ilimitadas.</span>
                  : <span>{FREE_LIMIT - generationCount} generaciones gratuitas restantes.</span>
                }
              </div>
            )}

            {/* Generate button */}
            <button
              onClick={handleGenerate}
              disabled={isGenerating || !prompt.trim() || !canGenerate}
              className="w-full py-4 bg-purple-600 hover:bg-purple-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-white font-mono uppercase tracking-widest text-sm rounded-xl transition-all flex items-center justify-center gap-2"
            >
              {isGenerating
                ? <><Loader2 className="w-5 h-5 animate-spin" /> Generando...</>
                : <><Wand2 className="w-5 h-5" /> Generar imagen</>
              }
            </button>

            {/* Error */}
            <AnimatePresence>
              {error && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-sm font-mono">
                  <X className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                  <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* RIGHT: Preview */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex flex-col">
            <AnimatePresence mode="wait">
              {isGenerating ? (
                <motion.div key="loading"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="flex-1 min-h-[400px] bg-zinc-900 border border-zinc-800 rounded-2xl flex flex-col items-center justify-center gap-6"
                >
                  <div className="relative w-20 h-20">
                    <div className="absolute inset-0 rounded-full border-2 border-purple-500/20 animate-ping" />
                    <div className="absolute inset-2 rounded-full border-2 border-purple-500/40 animate-ping" style={{ animationDelay: '0.3s' }} />
                    <div className="absolute inset-4 flex items-center justify-center">
                      <Wand2 className="w-8 h-8 text-purple-500 animate-pulse" />
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="font-mono text-purple-400 text-sm">Generando tu imagen...</p>
                    <p className="font-mono text-zinc-600 text-xs mt-1">Esto puede tardar unos segundos</p>
                  </div>
                </motion.div>
              ) : generatedImage ? (
                <motion.div key="result"
                  initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                  className="flex-1 flex flex-col gap-4"
                >
                  <div className="relative rounded-2xl overflow-hidden border border-purple-500/20 bg-zinc-900 group">
                    <img
                      src={generatedImage}
                      alt="Generated"
                      className="w-full object-contain cursor-zoom-in"
                      onClick={() => setShowDetailModal(true)}
                    />
                    {/* Hover overlay */}
                    <div
                      onClick={() => setShowDetailModal(true)}
                      className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all cursor-zoom-in flex items-center justify-center opacity-0 group-hover:opacity-100"
                    >
                      <div className="flex items-center gap-2 bg-zinc-900/80 backdrop-blur px-4 py-2 rounded-full border border-zinc-700">
                        <Maximize2 className="w-4 h-4 text-white" />
                        <span className="text-white font-mono text-xs">Ver detalles</span>
                      </div>
                    </div>
                    <div className="absolute top-3 right-3 flex gap-2">
                      <button onClick={() => setGeneratedImage(null)} className="p-2 bg-zinc-900/80 backdrop-blur rounded-lg text-zinc-400 hover:text-white border border-zinc-700 transition-all">
                        <RefreshCw className="w-4 h-4" />
                      </button>
                      <div className="flex items-center gap-2">
                      {user && (
                      <button onClick={() => setShowPublishModal(true)}
                          className="flex items-center gap-1.5 px-3 py-2 bg-blue-600/80 backdrop-blur rounded-lg text-white border border-blue-500 transition-all hover:bg-blue-500 font-mono text-xs">
                            <Globe className="w-3.5 h-3.5" /> Publicar
                        </button>
                      )}
                      {isPro && (
                        <button onClick={handleDownload} className="p-2 bg-purple-600/80 backdrop-blur rounded-lg text-white border border-purple-500 transition-all hover:bg-purple-500">
                          <Download className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    </div>
                    {!isPro && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-zinc-950 to-transparent p-4 flex items-end justify-between">
                        <span className="font-mono text-xs text-zinc-500">Marca de agua en plan gratuito</span>
                        <button onClick={() => onNavigate('precios')} className="flex items-center gap-1 text-xs font-mono bg-yellow-500 hover:bg-yellow-400 text-black px-3 py-1.5 rounded-lg transition-all">
                          <Crown className="w-3 h-3" /> Pro
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="font-mono text-xs text-zinc-600 italic">"{prompt}"</p>
                </motion.div>
              ) : (
                <motion.div key="empty"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="flex-1 min-h-[400px] bg-zinc-900 border-2 border-dashed border-zinc-800 rounded-2xl flex flex-col items-center justify-center gap-4 text-center p-8"
                >
                  <div className="w-20 h-20 rounded-2xl bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                    <ImageIcon className="w-10 h-10 text-zinc-600" />
                  </div>
                  <div>
                    <p className="font-heading text-lg font-bold text-zinc-500">Tu imagen aparecerá aquí</p>
                    <p className="text-zinc-600 text-sm mt-1">Escribe una descripción y pulsa Generar</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Pro banner */}
        {!isPro && (
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mt-12 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-4 justify-between"
          >
            <div className="text-center sm:text-left">
              <p className="font-heading text-xl font-bold mb-1">Desbloquea Veritas Image Pro</p>
              <p className="text-zinc-400 text-sm">Generaciones ilimitadas, mayor resolución, sin marcas de agua y descarga directa.</p>
            </div>
            <button onClick={() => onNavigate('precios')}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white font-mono uppercase tracking-wider px-6 py-3 rounded-xl transition-all whitespace-nowrap flex-shrink-0">
              <Crown className="w-4 h-4" /> Ver planes
            </button>
          </motion.div>
        )}
      </main>

      <Footer onNavigate={onNavigate} currentPage="image" />

      {/* Detail Modal — estilo Midjourney */}
      <AnimatePresence>
        {showDetailModal && generatedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setShowDetailModal(false); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-5xl bg-zinc-950 border border-zinc-800 rounded-2xl overflow-hidden flex flex-col lg:flex-row max-h-[90vh]"
            >
              {/* Imagen */}
              <div className="flex-1 min-w-0 flex items-center justify-center bg-zinc-900 relative">
                <img
                  src={generatedImage}
                  alt="Generated"
                  className="max-w-full max-h-[90vh] object-contain"
                />
                {/* Top bar sobre imagen */}
                <div className="absolute top-3 right-3 flex gap-2">
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="p-2 bg-zinc-900/80 backdrop-blur rounded-lg text-zinc-400 hover:text-white border border-zinc-700 transition-all"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Panel derecho */}
              <div className="w-full lg:w-80 flex-shrink-0 flex flex-col border-t lg:border-t-0 lg:border-l border-zinc-800 overflow-y-auto">
                {/* Header panel */}
                <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                  <div className="flex gap-2">
                    {/* Download */}
                    <button
                      onClick={handleDownload}
                      className="flex items-center gap-1.5 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg text-zinc-300 font-mono text-xs transition-all"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download
                    </button>
                    {/* Bookmark */}
                    <button className="p-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg text-zinc-400 hover:text-white transition-all">
                      <Bookmark className="w-3.5 h-3.5" />
                    </button>
                    {/* Share */}
                    <button
                      onClick={() => { navigator.clipboard?.writeText(generatedImage); }}
                      className="p-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg text-zinc-400 hover:text-white transition-all"
                    >
                      <Share2 className="w-3.5 h-3.5" />
                    </button>
                    {/* Delete */}
                    <button
                      onClick={() => { setShowDetailModal(false); setGeneratedImage(null); }}
                      className="p-2 bg-zinc-800 hover:bg-red-900/40 border border-zinc-700 rounded-lg text-zinc-400 hover:text-red-400 transition-all"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Prompt */}
                <div className="p-4 border-b border-zinc-800">
                  <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-2">Prompt</p>
                  <p className="text-sm text-zinc-200 leading-relaxed">{currentPrompt}</p>
                </div>

                {/* Settings */}
                <div className="p-4 border-b border-zinc-800">
                  <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-3">Settings</p>
                  <div className="space-y-2.5">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-zinc-500 font-mono">Model</span>
                      <span className="text-xs text-zinc-200 font-mono font-bold">Nano Banana 2</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-zinc-500 font-mono">Aspect ratio</span>
                      <div className="flex items-center gap-1.5">
                        <div className={`border border-zinc-500 rounded-sm ${
                          ratio === '1:1' ? 'w-3 h-3' :
                          ratio === '16:9' ? 'w-4 h-2.5' :
                          ratio === '9:16' ? 'w-2.5 h-4' : 'w-3.5 h-2.5'
                        }`} />
                        <span className="text-xs text-zinc-200 font-mono">{ratio}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-zinc-500 font-mono">Style</span>
                      <span className="text-xs text-zinc-200 font-mono capitalize">{style || 'Sin estilo'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-zinc-500 font-mono">Quality</span>
                      <span className="text-xs text-zinc-200 font-mono">2K</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-zinc-500 font-mono">File type</span>
                      <span className="text-xs text-zinc-200 font-mono">JPEG</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-zinc-500 font-mono">Date created</span>
                      <span className="text-xs text-zinc-200 font-mono">
                        {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="p-4 space-y-2 mt-auto">
                  {/* Video */}
                  <button
                    onClick={() => { setShowDetailModal(false); onNavigate('video'); }}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-xl text-zinc-300 font-mono text-sm transition-all"
                  >
                    <Video className="w-4 h-4" /> Video
                  </button>
                  {/* Edit */}
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setReferenceImage(generatedImage);
                      setReferenceImagePreview(generatedImage);
                    }}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-xl text-zinc-300 font-mono text-sm transition-all"
                  >
                    <Wand2 className="w-4 h-4" /> Edit
                  </button>
                  {/* Recreate */}
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setPrompt(currentPrompt);
                      setTimeout(() => handleGenerate(), 100);
                    }}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-xl text-zinc-300 font-mono text-sm transition-all"
                  >
                    <RotateCcw className="w-4 h-4" /> Recreate
                  </button>
                  {/* Upscale */}
                  <button
                    onClick={() => {
                      // Upscale: regenrar con prompt mejorado
                      setShowDetailModal(false);
                      setPrompt(currentPrompt + ', ultra detailed, 8K resolution, masterpiece');
                      setTimeout(() => handleGenerate(), 100);
                    }}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-purple-600 hover:bg-purple-500 border border-purple-500 rounded-xl text-white font-mono text-sm transition-all"
                  >
                    <ZoomIn className="w-4 h-4" /> Upscale
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Publish Modal */}
      <AnimatePresence>
        {showPublishModal && generatedImage && (
          <PublishModal
            mediaUrl={generatedImage}
            mediaType="image"
            prompt={currentPrompt}
            style={style}
            aspectRatio={ratio}
            token={localStorage.getItem('veritas_token')}
            onClose={() => setShowPublishModal(false)}
            onPublished={() => {
              setShowPublishModal(false);
              onNavigate('content');
            }}
          />
        )}
      </AnimatePresence>

      {/* History Sidebar */}
      <AnimatePresence>
        {showHistory && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 z-40" onClick={() => setShowHistory(false)} />
            <motion.aside initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25 }} className="fixed right-0 top-0 bottom-0 w-full sm:max-w-md bg-zinc-900 border-l border-zinc-800 z-50 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-zinc-800">
                <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                  <History className="w-5 h-5 text-purple-500" />
                  Historial de imágenes
                </h3>
                <div className="flex items-center gap-2">
                  {imageHistory.length > 0 && (
                    <button onClick={() => {
                      setImageHistory([]);
                      localStorage.removeItem('veritas_image_history');
                    }} className="text-zinc-500 hover:text-red-500 transition-colors p-2">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => setShowHistory(false)} className="text-zinc-500 hover:text-white transition-colors p-2">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {imageHistory.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <ImageIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="font-mono text-sm">No hay imágenes generadas</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {imageHistory.map((item) => (
                      <motion.div key={item.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                        className="group relative rounded-xl overflow-hidden border border-zinc-800 hover:border-purple-500/40 cursor-pointer transition-all bg-zinc-800"
                      >
                        {/* Sin imagen guardada — solo metadatos */}
                        <div className="w-full aspect-square flex flex-col items-center justify-center gap-2 p-3">
                          <ImageIcon className="w-8 h-8 text-zinc-600" />
                          <p className="text-xs text-zinc-400 font-mono text-center line-clamp-3 leading-tight">{item.prompt}</p>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-zinc-950 to-transparent p-2">
                          <p className="text-xs text-zinc-500">{new Date(item.created_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const updated = imageHistory.filter(h => h.id !== item.id);
                            setImageHistory(updated);
                            localStorage.setItem('veritas_image_history', JSON.stringify(updated));
                          }}
                          className="absolute top-2 right-2 p-1 bg-zinc-900/80 rounded-lg text-zinc-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
